// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ApprovalRequestForm.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ApprovalRequestComposer.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chat.test.tsx
import { useMemo, useState } from "react";

import {
  ApprovalRequestForm,
  type ApprovalCardHighlightTarget,
  type ApprovalRequestAction,
} from "./ApprovalRequestForm";

import {
  useDeclineProjectInvitation,
  useParticipationTasks,
  useProjectReviews,
  useRecordProjectPosition,
} from "../../../utils/queries/askPunttProjects";
import {
  buildApprovalAssetsByVersionId,
  filterProjectFeedParticipationTasks,
} from "../utils/participationApprovalTasks";

/**
 * Shared-project-chat accessory: loads open participation tasks for this
 * project and submits approve / request-changes / not-needed via approval
 * events (does not send a chat message — keeps the agent asleep).
 *
 * Mounted above the text composer (outside the bordered input card) so it
 * reads as an in-feed decision card, not part of the message box.
 */
export function ApprovalRequestComposer({
  projectId,
  currentUserId,
  onCardHighlight,
}: {
  projectId: string;
  currentUserId?: string | null;
  onCardHighlight?: (target: ApprovalCardHighlightTarget | null) => void;
}) {
  const tasksQuery = useParticipationTasks({
    projectId,
    status: "open",
    enabled: !!projectId,
  });
  const reviewsQuery = useProjectReviews(projectId, {
    enabled: !!projectId,
  });
  const reviews = reviewsQuery.data?.reviews ?? [];
  // Scope to this project and drop rows that would only render the generic
  // "Review asset" placeholder. Critical: when reviews are IDLE/empty,
  // orphan/stale open tasks must yield zero cards — not two placeholders.
  const tasks = useMemo(
    () =>
      filterProjectFeedParticipationTasks(
        tasksQuery.data?.tasks ?? [],
        projectId,
        reviews,
      ),
    [tasksQuery.data?.tasks, projectId, reviews],
  );
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);
  const recordPosition = useRecordProjectPosition(projectId);
  const declineInvitation = useDeclineProjectInvitation(projectId);

  const assetsByVersionId = useMemo(
    () => buildApprovalAssetsByVersionId(tasks, reviews),
    [tasks, reviews],
  );

  if (tasks.length === 0) return null;

  const submit = async (input: {
    task: (typeof tasks)[number];
    action: ApprovalRequestAction;
    noteBody?: string;
  }) => {
    const { task, action, noteBody } = input;
    setBusyVersionId(task.versionId);
    const idempotencyKey = `app:composer:${action}:${projectId}:${task.versionId}:${currentUserId ?? "me"}:${Date.now()}`;
    try {
      if (action === "not_needed") {
        await declineInvitation.mutateAsync({
          versionId: task.versionId,
          fileId: task.fileId,
          ticketId: task.ticketId,
          idempotencyKey,
        });
      } else {
        await recordPosition.mutateAsync({
          versionId: task.versionId,
          fileId: task.fileId,
          position: action,
          noteBody,
          ticketId: task.ticketId,
          idempotencyKey,
        });
      }
    } finally {
      setBusyVersionId(null);
    }
  };

  return (
    <ApprovalRequestForm
      tasks={tasks}
      busyVersionId={busyVersionId}
      assetsByVersionId={assetsByVersionId}
      onCardHighlight={onCardHighlight}
      onRespond={(input) => {
        void submit(input);
      }}
    />
  );
}
